import React, { useMemo, useState } from "react";
import {
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  Package,
  User,
  Layers,
  ArrowDownLeft,
  Ban,
  AlertTriangle,
  FlagTriangleRight,
  Eye,
} from "lucide-react";
import { IThirdPartyRequest } from "../../../models/thirdPartyRequest.model";
import { PaginationRes } from "../../../models/response.model";

interface ThirdPartyRequestsPageProps {
  requests: IThirdPartyRequest[];
  loading: boolean;
  pagination?: PaginationRes;
  statusTab: string;
  vendorFilter: string;
  page: number;
  onStatusTabChange: (status: string) => void;
  onVendorFilterChange: (vendorId: string) => void;
  onPageChange: (page: number) => void;
  onInwardToStore?: (request: IThirdPartyRequest) => void;
  onCancelRequest?: (request: IThirdPartyRequest) => void;
  onCompleteInward?: (
    request: IThirdPartyRequest,
    costs: {
      productionCost?: number;
      receivingTransportCost?: number;
      receivingExtraCost?: number;
    },
  ) => void;
  onViewBatches?: (request: IThirdPartyRequest) => void;
}

export default function ThirdPartyRequestsPage({
  requests,
  loading,
  pagination,
  statusTab,
  vendorFilter,
  page,
  onStatusTabChange,
  onVendorFilterChange,
  onPageChange,
  onInwardToStore,
  onCancelRequest,
  onCompleteInward,
  onViewBatches,
}: ThirdPartyRequestsPageProps) {
  // Store expanded item row IDs
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // State to handle request cancellation confirmation modal
  const [selectedRequestToCancel, setSelectedRequestToCancel] =
    useState<IThirdPartyRequest | null>(null);

  // State to handle finalize/complete batch confirmation modal
  const [selectedRequestToComplete, setSelectedRequestToComplete] =
    useState<IThirdPartyRequest | null>(null);
  const [completeCosts, setCompleteCosts] = useState({
    productionCost: "",
    receivingTransportCost: "",
    receivingExtraCost: "",
  });

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleConfirmCancel = () => {
    if (selectedRequestToCancel && onCancelRequest) {
      onCancelRequest(selectedRequestToCancel);
    }
    setSelectedRequestToCancel(null);
  };

  const handleOpenCompleteModal = (req: IThirdPartyRequest) => {
    setCompleteCosts({
      productionCost: "",
      receivingTransportCost: "",
      receivingExtraCost: "",
    });
    setSelectedRequestToComplete(req);
  };

  const handleConfirmComplete = () => {
    if (selectedRequestToComplete && onCompleteInward) {
      const toNumber = (val: string) =>
        val.trim() === "" ? undefined : Number(val);
      onCompleteInward(selectedRequestToComplete, {
        productionCost: toNumber(completeCosts.productionCost),
        receivingTransportCost: toNumber(completeCosts.receivingTransportCost),
        receivingExtraCost: toNumber(completeCosts.receivingExtraCost),
      });
    }
    setSelectedRequestToComplete(null);
  };

  // Extract unique vendors for the filter dropdown
  const vendorOptions = useMemo(() => {
    const map = new Map<string, string>();
    requests.forEach((req) => {
      if (req.vendorId?._id && req.vendorId?.name) {
        map.set(req.vendorId._id, req.vendorId.name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [requests]);

  const statuses = [
    { id: "dispatched", label: "Dispatched", icon: Truck },
    { id: "completed", label: "Completed", icon: CheckCircle2 },
    { id: "cancelled", label: "Cancelled", icon: XCircle },
    { id: "all", label: "All Statuses", icon: Clock },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Top Filter Bar */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b border-slate-100 pb-4">
        {/* Status Pill Filters */}
        <div className="flex flex-wrap items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
          {statuses.map((item) => {
            const Icon = item.icon;
            const isActive = statusTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onStatusTabChange(item.id)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all ${
                  isActive
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                }`}
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Vendor Dropdown Filter */}
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
              <Building2 size={16} />
            </div>
            <select
              value={vendorFilter}
              onChange={(e) => onVendorFilterChange(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-2 pl-9 pr-8 text-sm text-slate-700 outline-none transition focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            >
              <option value="">All Vendors</option>
              {vendorOptions.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content View */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-600 border-t-transparent" />
        </div>
      ) : requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
          <div className="rounded-full bg-slate-100 p-4 text-slate-400">
            <Truck size={32} />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-slate-800">
            No requests found
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            There are no third-party requests matching the selected filters.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-6 py-3">Req No</th>
                  <th className="px-6 py-3">Vendor</th>
                  <th className="px-6 py-3">Warehouse</th>
                  <th className="px-6 py-3">Outward Items</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Created</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.map((req) => {
                  const reqId = req._id as string;
                  const isExpanded = !!expandedRows[reqId];
                  const itemsCount = req.outwardItems?.length || 0;
                  const isDispatched = req.status === "dispatched";

                  return (
                    <React.Fragment key={reqId}>
                      <tr className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-6 py-4 font-semibold text-slate-900">
                          {req.requestNo}
                        </td>
                        <td className="px-6 py-4 font-medium text-slate-700">
                          {req.vendorId?.name || "N/A"}
                        </td>
                        <td className="px-6 py-4">
                          {req.warehouseId?.name || "N/A"}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => toggleRow(reqId)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 font-medium text-xs hover:bg-indigo-100 transition border border-indigo-200/60"
                          >
                            <Layers size={14} />
                            <span>
                              {itemsCount} {itemsCount === 1 ? "" : "(s)"}
                            </span>
                            {isExpanded ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={req.status} />
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500">
                          {new Date(req.createdAt).toLocaleDateString()}
                        </td>

                        {/* Action Buttons */}
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() =>
                                onViewBatches && onViewBatches(req)
                              }
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 font-medium text-xs hover:bg-slate-50 hover:border-slate-300 transition"
                              title="View inward batches"
                            >
                              <Eye size={14} />
                              <span>Batches</span>
                            </button>
                            {isDispatched ? (
                              <>
                                <button
                                  onClick={() =>
                                    onInwardToStore && onInwardToStore(req)
                                  }
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-medium text-xs hover:bg-emerald-700 active:bg-emerald-800 transition shadow-2xs"
                                  title="Inward processed goods into store"
                                >
                                  <ArrowDownLeft size={14} />
                                  <span>Inward</span>
                                </button>
                                <button
                                  onClick={() => handleOpenCompleteModal(req)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-medium text-xs hover:bg-indigo-700 active:bg-indigo-800 transition shadow-2xs"
                                  title="Finalize and complete this batch"
                                >
                                  <FlagTriangleRight size={14} />
                                  <span>Complete</span>
                                </button>
                                <button
                                  onClick={() =>
                                    setSelectedRequestToCancel(req)
                                  }
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-rose-200 text-rose-600 font-medium text-xs hover:bg-rose-50 hover:border-rose-300 transition"
                                  title="Cancel this request"
                                >
                                  <Ban size={14} />
                                  <span>Cancel</span>
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>

                      {/* Expandable Outward Items Row */}
                      {isExpanded && (
                        <tr className="bg-slate-50/60 border-t border-slate-100">
                          <td colSpan={8} className="px-6 py-4">
                            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 shadow-2xs">
                              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                <span className="text-xs font-semibold uppercase text-slate-500 tracking-wider flex items-center gap-1">
                                  <Package size={14} /> Outward Material Details
                                </span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {req.outwardItems?.map((item, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50"
                                  >
                                    <div>
                                      <p className="font-semibold text-slate-800 text-sm">
                                        {item.rawMaterialId?.name || "Material"}
                                      </p>
                                      {item.inwardAllocations &&
                                        item.inwardAllocations.length > 0 && (
                                          <p className="text-[11px] text-slate-400 mt-0.5">
                                            Allocations:{" "}
                                            {item.inwardAllocations.length}{" "}
                                            batch(es)
                                          </p>
                                        )}
                                    </div>
                                    <span className="font-bold text-slate-900 bg-white px-2.5 py-1 rounded-md border border-slate-200 text-xs">
                                      {item.quantity} {item.unit}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile & Tablet Card View */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:hidden gap-4">
            {requests.map((req) => {
              const reqId = req._id as string;
              const isExpanded = !!expandedRows[reqId];
              const itemsCount = req.outwardItems?.length || 0;
              const isDispatched = req.status === "dispatched";

              return (
                <div
                  key={reqId}
                  className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-xs hover:shadow-md transition"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                          Request ID
                        </span>
                        <p className="font-bold text-slate-800">
                          {req.requestNo}
                        </p>
                      </div>
                      <StatusBadge status={req.status} />
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building2
                          size={16}
                          className="text-slate-400 shrink-0"
                        />
                        <span className="truncate">
                          {req.vendorId?.name || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Package
                          size={16}
                          className="text-slate-400 shrink-0"
                        />
                        <span className="truncate">
                          {req.warehouseId?.name || "N/A"}
                        </span>
                      </div>
                    </div>

                    {/* Expandable Outward Button */}
                    <button
                      onClick={() => toggleRow(reqId)}
                      className="w-full flex items-center justify-between p-3 rounded-xl bg-indigo-50/70 border border-indigo-100 text-indigo-800 text-xs font-semibold hover:bg-indigo-100 transition"
                    >
                      <div className="flex items-center gap-2">
                        <Layers size={16} />
                        <span>Outward Items ({itemsCount})</span>
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </button>

                    {/* Expanded Mobile List */}
                    {isExpanded && (
                      <div className="space-y-2 pt-1">
                        {req.outwardItems?.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-slate-50 text-xs"
                          >
                            <span className="font-medium text-slate-700">
                              {item.rawMaterialId?.name}
                            </span>
                            <span className="font-bold text-slate-900 bg-white px-2 py-0.5 rounded border border-slate-200">
                              {item.quantity} {item.unit}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Mobile Actions Footer */}
                  <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                    <button
                      onClick={() => onViewBatches && onViewBatches(req)}
                      className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium text-xs hover:bg-slate-50 transition"
                    >
                      <Eye size={16} /> Batches
                    </button>
                    {isDispatched && (
                      <>
                        <button
                          onClick={() =>
                            onInwardToStore && onInwardToStore(req)
                          }
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-600 text-white font-medium text-xs hover:bg-emerald-700 transition"
                        >
                          <ArrowDownLeft size={16} /> Inward to Store
                        </button>
                        <button
                          onClick={() => handleOpenCompleteModal(req)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-indigo-600 text-white font-medium text-xs hover:bg-indigo-700 transition"
                        >
                          <FlagTriangleRight size={16} /> Complete
                        </button>
                        <button
                          onClick={() => setSelectedRequestToCancel(req)}
                          className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-rose-200 text-rose-600 font-medium text-xs hover:bg-rose-50 transition"
                        >
                          <Ban size={16} /> Cancel
                        </button>
                      </>
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <User size={14} />
                      <span>{req.createdBy?.username || "Admin"}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>
                        {new Date(req.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 pt-4 text-sm text-slate-600">
              <p>
                Showing Page{" "}
                <span className="font-semibold">{pagination.page}</span> of{" "}
                <span className="font-semibold">{pagination.totalPages}</span> (
                {pagination.totalCount} total)
              </p>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => onPageChange(page - 1)}
                  className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                <button
                  disabled={page >= pagination.totalPages}
                  onClick={() => onPageChange(page + 1)}
                  className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      {selectedRequestToCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-rose-100 p-2.5 text-rose-600">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Cancel Request
                </h3>
                <p className="text-xs text-slate-500">
                  Request No:{" "}
                  <span className="font-semibold text-slate-700">
                    {selectedRequestToCancel.requestNo}
                  </span>
                </p>
              </div>
            </div>

            <p className="text-sm text-slate-600">
              Are you sure you want to cancel this request? This action cannot
              be undone.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setSelectedRequestToCancel(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                No, Keep It
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 transition"
              >
                Yes, Cancel Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize/Complete Batch Modal */}
      {selectedRequestToComplete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-indigo-100 p-2.5 text-indigo-600">
                <FlagTriangleRight size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Finalize batch?
                </h3>
                <p className="text-xs text-slate-500">
                  Request No:{" "}
                  <span className="font-semibold text-slate-700">
                    {selectedRequestToComplete.requestNo}
                  </span>
                </p>
              </div>
            </div>

            <p className="text-sm text-slate-600">
              This will mark the third-party inward as complete. You can
              optionally add any additional costs incurred, on top of what was
              already recorded per batch.
            </p>

            <div className="grid grid-cols-1 gap-3">
              <label className="text-xs font-medium text-slate-600">
                Production Cost
                <input
                  type="number"
                  min={0}
                  value={completeCosts.productionCost}
                  onChange={(e) =>
                    setCompleteCosts((prev) => ({
                      ...prev,
                      productionCost: e.target.value,
                    }))
                  }
                  placeholder="Optional"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
              <label className="text-xs font-medium text-slate-600">
                Receiving Transport Cost
                <input
                  type="number"
                  min={0}
                  value={completeCosts.receivingTransportCost}
                  onChange={(e) =>
                    setCompleteCosts((prev) => ({
                      ...prev,
                      receivingTransportCost: e.target.value,
                    }))
                  }
                  placeholder="Optional"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
              <label className="text-xs font-medium text-slate-600">
                Receiving Extra Cost
                <input
                  type="number"
                  min={0}
                  value={completeCosts.receivingExtraCost}
                  onChange={(e) =>
                    setCompleteCosts((prev) => ({
                      ...prev,
                      receivingExtraCost: e.target.value,
                    }))
                  }
                  placeholder="Optional"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setSelectedRequestToComplete(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmComplete}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition"
              >
                Yes, Finalize Batch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "dispatched":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 border border-amber-200/60">
          <Truck size={12} /> Dispatched
        </span>
      );
    case "completed":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 border border-emerald-200/60">
          <CheckCircle2 size={12} /> Completed
        </span>
      );
    case "cancelled":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 border border-rose-200/60">
          <XCircle size={12} /> Cancelled
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          {status}
        </span>
      );
  }
}
